import * as React from 'react';

import { PlanType } from '@showbie-socrative/socrative-utils';
import { translate } from '~l10n/client';

import { Divider } from '~shared/components/Divider';
import { IconCheckmark } from '~shared/components/icons/micro';
import { TooltipV2 } from '~shared/components/TooltipV2';
import { BillingPeriod } from '~teacher/components/PricingTable/BillingPeriod';
import { PlanBadge } from '~teacher/components/PricingTable/PlanBadge';
import { EssentialsPriceComponent } from '~teacher/components/PricingTable/PriceComponent/EssentialsPriceComponent';
import { FreePriceComponent } from '~teacher/components/PricingTable/PriceComponent/FreePriceComponent';
import { OrganizationPriceComponent } from '~teacher/components/PricingTable/PriceComponent/OrganizationPriceComponent';
import { ProPriceComponent } from '~teacher/components/PricingTable/PriceComponent/ProPriceComponent';

export type PlanColumnFeature = {
  text: string;
  tooltip?: string;
  link?: string;
};

interface PlanColumnProps {
  isBestForTeams?: boolean;
  billingPeriod: BillingPeriod;
  planType: PlanType;
}

export function PlanColumn({
  isBestForTeams = false,
  billingPeriod,
  planType,
}: PlanColumnProps) {
  const { title, description, featureHeadingText, priceComponent, features } =
    getPlanColumnConfig({ billingPeriod, planType });

  return (
    <>
      <div>
        {isBestForTeams ? (
          <p className="flex items-center text-xs font-sans font-700 text-orange-700 leading-8 h-6 uppercase">
            {translate('Best for Teams')}
          </p>
        ) : null}
      </div>
      <div className="plan-title">
        <div className="flex items-center gap-2">
          <h2 className="font-display text-md font-700 text-blue-900 leading-10">
            {title}
          </h2>
          <div>
            <PlanBadge targetPlan={planType} />
          </div>
        </div>
      </div>
      <div className="text-base text-blue-900 pb-12 leading-8 font-sans">
        {description}
      </div>
      <div className="plan-price grid row-span-5 grid-rows-subgrid">
        {priceComponent}
      </div>
      <div className="plan-features pt-12">
        <div className="text-base font-sans font-600 text-blue-900 leading-6 pb-6">
          {featureHeadingText}
        </div>
        <ul>
          {features.map((feature, index) => {
            return (
              <li key={`feature-${index}`} className={'flex items-start mb-3'}>
                <IconCheckmark className={'flex-shrink-0 text-cyan-700 mt-1'} />
                {(() => {
                  if (feature.tooltip) {
                    return (
                      <TooltipV2 direction={'top'} label={feature.tooltip}>
                        <span
                          className={
                            'text-base text-blue-900 leading-8' +
                            ' font-sans tooltip-highlight ml-3'
                          }
                        >
                          {feature.text}
                        </span>
                      </TooltipV2>
                    );
                  } else {
                    return (
                      <span
                        className={
                          'text-base text-blue-900 leading-8' +
                          ' font-sans ml-3'
                        }
                      >
                        {feature.text}
                      </span>
                    );
                  }
                })()}
              </li>
            );
          })}
        </ul>
        <div className="mobile-divider my-12 py-3">
          <Divider />
        </div>
      </div>
    </>
  );
}

interface PlanColumnConfig {
  title: string;
  description: string;
  featureHeadingText: string;
  features: Array<PlanColumnFeature>;
  priceComponent: JSX.Element;
}

interface GetPlanColumnConfigParams {
  planType: PlanType;
  billingPeriod: BillingPeriod;
}

function getPlanColumnConfig({
  billingPeriod,
  planType,
}: GetPlanColumnConfigParams): PlanColumnConfig {
  switch (planType) {
    case PlanType.Essentials: {
      const ROOMS_LIMIT = 5;
      const REPORT_HISTORY = 90;

      return {
        title: 'Essentials',
        description: translate(
          'For individuals assessing a small number of participants.'
        ),
        featureHeadingText: translate('Everything in {0}, plus:').format(
          'Free'
        ),
        priceComponent: (
          <EssentialsPriceComponent billingPeriod={billingPeriod} />
        ),
        features: [
          { text: translate('Unlimited quizzes') },
          {
            text: translate('{0} rooms').format(ROOMS_LIMIT),
            tooltip: translate(
              'Use rooms to launch multiple activities and organize your classes'
            ),
          },
          { text: translate('{0}-day report history').format(REPORT_HISTORY) },
          { text: translate('Give partial marks') },
          {
            text: translate('Pause activities'),
            tooltip: translate(
              'Pause student responses and hide questions until ready to resume'
            ),
          },
          {
            text: translate('Rostered rooms'),
            tooltip: translate(
              'Send activity results to students and manage their access to activities'
            ),
          },
          {
            text: translate('Advanced reports'),
            tooltip: translate(
              'Reveal an explanation for the correct answer, download the answer key, and email results to students'
            ),
          },
          {
            text: translate('Enhanced access to Socrative AI'),
            tooltip:
              translate(
                'Save time by describing your quiz and AI will create your first draft.'
              ) +
              ' ' +
              translate(
                'Paste questions from another resource and convert them into a quiz.'
              ),
          },
        ],
      };
    }
    case PlanType.Pro: {
      const ROOMS_LIMIT = 30;
      const STUDENTS_PER_ROOM = 300;
      const SHARED_LIBRARY_LIMIT = 20;

      return {
        title: 'Pro',
        description: translate(
          'For teams assessing at scale with advanced tools.'
        ),
        featureHeadingText: translate('Everything in {0}, plus:').format(
          'Essentials'
        ),
        priceComponent: <ProPriceComponent billingPeriod={billingPeriod} />,
        features: [
          {
            text: translate('{0} rooms').format(ROOMS_LIMIT),
            tooltip: translate(
              'Use rooms to launch multiple activities and organize your classes'
            ),
          },
          {
            text: translate('{0} shared libraries').format(
              SHARED_LIBRARY_LIMIT
            ),
            tooltip: translate(
              'Co-create and share quiz content with colleagues'
            ),
          },
          {
            text: translate('{0} students per activity').format(
              STUDENTS_PER_ROOM
            ),
          },
          { text: translate('Embed Videos') },
          { text: translate('Unlimited report history') },
          {
            text: translate('Full access to Socrative AI'),
            tooltip:
              translate(
                'Save time by describing your quiz and AI will create your first draft.'
              ) +
              ' ' +
              translate(
                'Paste questions from another resource and convert them into a quiz.'
              ),
          },
        ],
      };
    }
    case PlanType.Organization: {
      return {
        title: 'Organization',
        description:
          translate(
            'For organizations standardizing and scaling assessments across multiple teams'
          ) + '.',
        featureHeadingText: translate('Everything in {0}, plus:').format('Pro'),
        priceComponent: <OrganizationPriceComponent />,
        features: [
          {
            text: translate('More savings per seat'),
          },
          {
            text: translate('Multi-account consolidation'),
          },
          {
            text: translate('Enhanced control of shared resources'),
          },
          {
            text: translate('Additional payment options'),
            tooltip: translate(
              'Purchase orders, checks and bank transfers accepted.'
            ),
          },
          {
            text: translate('Dedicated account manager'),
          },
          {
            text: translate('Priority support'),
          },
          {
            text: translate('Custom agreements'),
            tooltip: translate(
              'Our team is available to review custom requests and documentation.'
            ),
          },
        ],
      };
    }
    case PlanType.Free: {
      const QUIZZES_LIMIT = 5;
      const STUDENTS_PER_ROOM = 50;
      const REPORT_HISTORY = 30;

      return {
        title: 'Free',
        description: translate('For individuals just getting started.'),
        featureHeadingText: translate('What’s included:'),
        priceComponent: <FreePriceComponent />,
        features: [
          { text: translate('{0} quizzes').format(QUIZZES_LIMIT) },
          {
            text: translate('1 room'), // Singular room
            tooltip: translate(
              'Use rooms to launch multiple activities and organize your classes'
            ),
          },
          {
            text: translate('{0} students per activity').format(
              STUDENTS_PER_ROOM
            ),
          },
          { text: translate('{0}-day report history').format(REPORT_HISTORY) },
          { text: translate('Automatic grading') },
          {
            text: translate('Basic access to Socrative AI'),
            tooltip:
              translate(
                'Save time by describing your quiz and AI will create your first draft.'
              ) +
              ' ' +
              translate(
                'Paste questions from another resource and convert them into a quiz.'
              ),
          },
        ],
      };
    }
    default:
      return null;
  }
}
